/**
 * A pooled particle system in world coordinates (blocks): death explosions, orb and pad
 * bursts, pickup sparkles, landing dust, a spider's teleport streak, saw blades' sparks,
 * level-complete confetti.
 *
 * Particles are stored in fixed typed arrays (structure of arrays) and removed by swapping
 * with the last one, so effects never allocate while the game runs.
 */
import type { ViewTransform } from './view';

const CAPACITY = 420;

const SQUARE = 0;
const CIRCLE = 1;
const SPARK = 2;
const RING = 3;
const CONFETTI = 4;
/** A vertical streak from (x, y) `angle` blocks up (or down, negative); it does not move. */
const BEAM = 5;
type Shape =
  | typeof SQUARE
  | typeof CIRCLE
  | typeof SPARK
  | typeof RING
  | typeof CONFETTI
  | typeof BEAM;

export const CONFETTI_COLORS = ['#ff3b5c', '#ffd60a', '#3dff8b', '#2de2ff', '#ff4fd8', '#ffffff'];

export class Particles {
  private readonly x = new Float64Array(CAPACITY);
  private readonly y = new Float64Array(CAPACITY);
  private readonly vx = new Float32Array(CAPACITY);
  private readonly vy = new Float32Array(CAPACITY);
  private readonly life = new Float32Array(CAPACITY);
  private readonly maxLife = new Float32Array(CAPACITY);
  private readonly size = new Float32Array(CAPACITY);
  private readonly angle = new Float32Array(CAPACITY);
  private readonly spin = new Float32Array(CAPACITY);
  private readonly gravity = new Float32Array(CAPACITY);
  private readonly drag = new Float32Array(CAPACITY);
  private readonly shape = new Uint8Array(CAPACITY);
  private readonly color = new Uint8Array(CAPACITY);
  /** Colours by index (a particle stores the index; the list only grows to the few in use). */
  private readonly palette: string[] = [];
  private count = 0;

  get active(): number {
    return this.count;
  }

  clear(): void {
    this.count = 0;
  }

  /** The racer exploded: squares in its colours, sparks and a shock ring. */
  explosion(x: number, y: number, primary: string, secondary: string): void {
    for (let i = 0; i < 26; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 3 + Math.random() * 9;
      this.emit(
        SQUARE,
        x,
        y,
        Math.cos(a) * speed,
        Math.sin(a) * speed + 2,
        0.5 + Math.random() * 0.5,
        0.12 + Math.random() * 0.22,
        i % 3 === 0 ? secondary : primary,
        -14,
        1.2,
        (Math.random() - 0.5) * 18,
      );
    }
    for (let i = 0; i < 14; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 8 + Math.random() * 10;
      this.emit(
        SPARK,
        x,
        y,
        Math.cos(a) * speed,
        Math.sin(a) * speed,
        0.3,
        0.06,
        '#ffffff',
        0,
        3,
        0,
      );
    }
    this.ring(x, y, primary, 2.2, 0.4);
    this.ring(x, y, '#ffffff', 1.4, 0.25);
  }

  /** An expanding ring (orbs, pads, portals, respawn). */
  ring(x: number, y: number, color: string, radius: number, life: number): void {
    this.emit(RING, x, y, 0, 0, life, radius, color, 0, 0, 0);
  }

  /** Orb or pad used: a ring and a puff of sparks in its colour. */
  burst(x: number, y: number, color: string, upward: number): void {
    this.ring(x, y, color, 1.3, 0.35);
    for (let i = 0; i < 10; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      this.emit(
        CIRCLE,
        x,
        y,
        Math.cos(a) * speed,
        Math.sin(a) * speed + upward * 3,
        0.35 + Math.random() * 0.25,
        0.07 + Math.random() * 0.07,
        color,
        0,
        2.5,
        0,
      );
    }
  }

  /** A boost was picked up: twinkling stars rising in its colour. */
  sparkle(x: number, y: number, color: string): void {
    this.ring(x, y, color, 1.1, 0.3);
    for (let i = 0; i < 12; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;
      this.emit(
        SPARK,
        x + Math.cos(a) * 0.3,
        y + Math.sin(a) * 0.3,
        Math.cos(a) * speed,
        Math.sin(a) * speed + 2,
        0.45 + Math.random() * 0.3,
        0.05,
        i % 2 === 0 ? color : '#ffffff',
        -2,
        2,
        0,
      );
    }
  }

  /** Landing dust at the feet (gravity: which way is down). */
  dust(x: number, feetY: number, gravity: 1 | -1, color: string): void {
    for (let i = 0; i < 6; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      this.emit(
        CIRCLE,
        x + side * 0.3,
        feetY,
        side * (1 + Math.random() * 2) - 2,
        gravity * (0.5 + Math.random()),
        0.28 + Math.random() * 0.15,
        0.07 + Math.random() * 0.06,
        color,
        0,
        4,
        0,
      );
    }
  }

  /**
   * A spider teleported from (x, fromY) to (x, toY): a streak between the two in its colour
   * with a white core, left behind where it happened, a flash where it lands and sparks where
   * it left; `strength` < 1 makes it fainter (another racer's ghost).
   */
  teleport(x: number, fromY: number, toY: number, color: string, strength = 1): void {
    // The streak's length rides in its angle (a beam does not spin).
    const length = toY - fromY;
    this.angle[this.emit(BEAM, x, fromY, 0, 0, 0.28, 0.42 * strength, color, 0, 0, 0)] = length;
    this.angle[this.emit(BEAM, x, fromY, 0, 0, 0.18, 0.14 * strength, '#ffffff', 0, 0, 0)] = length;
    this.ring(x, toY, color, 1.2 * strength, 0.25);
    const away = toY > fromY ? -1 : 1;
    for (let i = 0; i < 6 * strength; i++) {
      this.emit(
        SPARK,
        x + (Math.random() - 0.5) * 0.6,
        fromY,
        (Math.random() - 0.5) * 6,
        away * (2 + Math.random() * 4),
        0.25,
        0.05,
        i % 2 === 0 ? color : '#ffffff',
        0,
        4,
        0,
      );
    }
  }

  /**
   * Sparks where a saw blade cuts into a surface at (x, y): a couple of hot sparks thrown back
   * along the surface and away from it (`side`: +1 up from a floor, −1 down from a ceiling),
   * falling down as they fade.
   */
  sawSparks(x: number, y: number, side: 1 | -1): void {
    for (let i = 0; i < 2; i++) {
      this.emit(
        SPARK,
        x + (Math.random() - 0.5) * 0.4,
        y + side * 0.05,
        -2 - Math.random() * 5,
        side * (1.5 + Math.random() * 4),
        0.22 + Math.random() * 0.18,
        0.045,
        i === 0 ? '#ffd27a' : '#ffffff',
        -18,
        1.5,
        0,
      );
    }
  }

  /** Confetti raining over the world range [x1, x2] from height `top`. */
  confetti(x1: number, x2: number, top: number): void {
    for (let i = 0; i < 70; i++) {
      this.emit(
        CONFETTI,
        x1 + Math.random() * (x2 - x1),
        top + Math.random() * 1.5,
        (Math.random() - 0.5) * 3,
        -1 - Math.random() * 3,
        1.6 + Math.random() * 1.2,
        0.14 + Math.random() * 0.1,
        CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
        -3,
        0.6,
        (Math.random() - 0.5) * 14,
      );
    }
  }

  /** Advances every particle by `dt` seconds. */
  step(dt: number): void {
    let i = 0;
    while (i < this.count) {
      const life = this.life[i]! - dt;
      if (life <= 0) {
        this.remove(i);
        continue;
      }
      this.life[i] = life;
      const drag = Math.max(0, 1 - this.drag[i]! * dt);
      this.vx[i] = this.vx[i]! * drag;
      this.vy[i] = (this.vy[i]! + this.gravity[i]! * dt) * drag;
      this.x[i] = this.x[i]! + this.vx[i]! * dt;
      this.y[i] = this.y[i]! + this.vy[i]! * dt;
      this.angle[i] = this.angle[i]! + this.spin[i]! * dt;
      i++;
    }
  }

  draw(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    if (this.count === 0) return;
    const ppb = view.ppb;
    for (let i = 0; i < this.count; i++) {
      const t = this.life[i]! / this.maxLife[i]!;
      const x = view.sx(this.x[i]!);
      const y = view.sy(this.y[i]!);
      const size = this.size[i]! * ppb;
      const color = this.palette[this.color[i]!]!;
      switch (this.shape[i]) {
        case SQUARE: {
          ctx.globalAlpha = Math.min(1, t * 1.6);
          ctx.fillStyle = color;
          const a = this.angle[i]!;
          const c = Math.cos(a) * size * 0.5;
          const s = Math.sin(a) * size * 0.5;
          ctx.beginPath();
          ctx.moveTo(x + c - s, y + s + c);
          ctx.lineTo(x - c - s, y - s + c);
          ctx.lineTo(x - c + s, y - s - c);
          ctx.lineTo(x + c + s, y + s - c);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = '#0a0614';
          ctx.lineWidth = Math.max(1, size * 0.12);
          ctx.stroke();
          break;
        }
        case CIRCLE:
          ctx.globalAlpha = t;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
          break;
        case SPARK: {
          ctx.globalAlpha = t;
          ctx.strokeStyle = color;
          ctx.lineWidth = Math.max(1, size);
          const k = 0.035 * ppb;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x - this.vx[i]! * k, y + this.vy[i]! * k);
          ctx.stroke();
          break;
        }
        case RING: {
          const radius = size * (1 - t * t);
          ctx.globalAlpha = t;
          ctx.strokeStyle = color;
          ctx.lineWidth = Math.max(1.5, ppb * 0.12 * t);
          ctx.beginPath();
          ctx.arc(x, y, Math.max(0.5, radius), 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case CONFETTI: {
          ctx.globalAlpha = Math.min(1, t * 2);
          ctx.fillStyle = color;
          const flutter = Math.abs(Math.cos(this.angle[i]!));
          ctx.fillRect(x - (size * flutter) / 2, y - size * 0.3, size * flutter + 1, size * 0.6);
          break;
        }
        case BEAM: {
          // Narrows as it fades.
          const to = view.sy(this.y[i]! + this.angle[i]!);
          const w = Math.max(1, size * t);
          ctx.globalAlpha = t;
          ctx.fillStyle = color;
          ctx.fillRect(x - w / 2, Math.min(y, to), w, Math.abs(to - y));
          break;
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  private emit(
    shape: Shape,
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    size: number,
    color: string,
    gravity: number,
    drag: number,
    spin: number,
  ): number {
    // When full, the newest effect wins over the oldest particle.
    const i = this.count < CAPACITY ? this.count++ : Math.floor(Math.random() * CAPACITY);
    this.x[i] = x;
    this.y[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.life[i] = life;
    this.maxLife[i] = life;
    this.size[i] = size;
    this.angle[i] = Math.random() * Math.PI;
    this.spin[i] = spin;
    this.gravity[i] = gravity;
    this.drag[i] = drag;
    this.shape[i] = shape;
    this.color[i] = this.colorIndex(color);
    return i;
  }

  private colorIndex(color: string): number {
    let index = this.palette.indexOf(color);
    if (index < 0) {
      if (this.palette.length >= 250) this.palette.length = 0;
      index = this.palette.push(color) - 1;
    }
    return index;
  }

  private remove(i: number): void {
    const last = --this.count;
    if (i === last) return;
    this.x[i] = this.x[last]!;
    this.y[i] = this.y[last]!;
    this.vx[i] = this.vx[last]!;
    this.vy[i] = this.vy[last]!;
    this.life[i] = this.life[last]!;
    this.maxLife[i] = this.maxLife[last]!;
    this.size[i] = this.size[last]!;
    this.angle[i] = this.angle[last]!;
    this.spin[i] = this.spin[last]!;
    this.gravity[i] = this.gravity[last]!;
    this.drag[i] = this.drag[last]!;
    this.shape[i] = this.shape[last]!;
    this.color[i] = this.color[last]!;
  }
}
